/**
 * Campaign dialog: pick (or create) a campaign — a sequenced series of
 * identical work orders such as a pharma batch series or a fleet upfit
 * order — then generate the next numbered batch of work orders onto the
 * plan. Repeat generations extend the sequence (#1-#5, then #6-#9).
 */
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import {
	Alert,
	Box,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Campaign, campaignsApi } from "../../../../services/campaigns-api";
import { LaborStandard, laborStandardsApi } from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import {
	WorkOrderAttributeDefinition,
	workOrderAttributesApi,
} from "../../../../services/work-order-attributes-api";
import {
	GhostButton,
	MonoText,
	planner,
	PlannerCard, // eslint-disable-line sort-imports
	PrimaryButton,
	ProgressMeter,
	SectionLabel,
	StaffingState,
} from "../../planner-ui";
import { ProductMenuEmpty, ProductMenuSearch, useProductMenuSearch } from "../../planner-ui/product-menu-search";
import { campaignAttributeDefaults } from "../utils/campaign-defaults";

// One recipe for every control in the dialog, so the app theme's primary blue
// never appears as a focus ring or floating label on a planning surface.
const formControlsSx = {
	"& .MuiOutlinedInput-root": {
		...planner.type.body,
		borderRadius: `${planner.radiusSm}px`,
		"& fieldset": { borderColor: planner.hairlineStrong },
		"&:hover fieldset": { borderColor: planner.navy },
		"&.Mui-focused fieldset": { borderColor: planner.accent },
	},
	"& .MuiInputLabel-root": { ...planner.type.meta, color: planner.inkMuted },
	"& .MuiInputLabel-root.Mui-focused": { color: planner.accent },
	"& .MuiFormHelperText-root": { ...planner.type.meta, color: planner.inkMuted },
};

interface CampaignDialogProps {
	open: boolean;
	onClose: () => void;
	companyId: number;
	products: Product[];
	workcenters: string[];
	/** Called after work orders were generated (reload the plan) */
	onGenerated: () => void;
	/** The plant on screen: the batches are made for it. */
	locationId?: number | null;
}

type CampaignMode = "existing" | "new";

interface GenerateResult {
	campaign: Campaign;
	createdCount: number;
	fromSeq: number;
	toSeq: number;
}

const campaignProgressState = (campaign: Campaign): StaffingState => {
	if (campaign.targetCount > 0 && campaign.generatedCount >= campaign.targetCount) {
		return "staffed";
	}
	return campaign.generatedCount > 0 ? "partial" : "unplanned";
};

const CampaignDialog: React.FC<CampaignDialogProps> = ({
	open,
	onClose,
	companyId,
	products,
	workcenters,
	onGenerated,
	locationId,
}) => {
	// Step 1 — campaign selection / creation
	const [campaigns, setCampaigns] = useState<Campaign[]>([]);
	const [loadingCampaigns, setLoadingCampaigns] = useState(false);
	const [mode, setMode] = useState<CampaignMode>("existing");
	const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
	const [name, setName] = useState("");
	const [productId, setProductId] = useState<number | "">("");
	const [laborStandardId, setLaborStandardId] = useState<number | "">("");
	const [targetCount, setTargetCount] = useState("");
	const [notes, setNotes] = useState("");
	const [laborStandards, setLaborStandards] = useState<LaborStandard[]>([]);
	const productSearch = useProductMenuSearch(products, productId);

	// Step 2 — generation parameters
	const [count, setCount] = useState("1");
	const [countTouched, setCountTouched] = useState(false);
	const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
	const [workstation, setWorkstation] = useState("");
	const [spacingDays, setSpacingDays] = useState("1");
	const [quantity, setQuantity] = useState("");
	const [attributeDefinitions, setAttributeDefinitions] = useState<WorkOrderAttributeDefinition[]>([]);
	const [attributeValues, setAttributeValues] = useState<Record<string, string>>({});
	// Fields the planner typed in: the product's own values never overwrite them.
	const [typedAttributes, setTypedAttributes] = useState<Set<string>>(() => new Set());

	const [generating, setGenerating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<GenerateResult | null>(null);

	const loadCampaigns = useCallback(async () => {
		setLoadingCampaigns(true);
		try {
			const data = await campaignsApi.getByCompany(companyId);
			setCampaigns(data);
		} catch (err: any) {
			console.error("Failed to fetch campaigns:", err);
			setError(err?.response?.data?.message || "Failed to load campaigns");
		} finally {
			setLoadingCampaigns(false);
		}
	}, [companyId]);

	// Load campaigns, labor standards and attribute definitions on open
	useEffect(() => {
		if (!open || !companyId) return;
		loadCampaigns();
		laborStandardsApi.getAll()
			.then((standards) => setLaborStandards(standards.filter((standard) => standard.isActive)))
			.catch((err) => {
				console.error("Failed to fetch labor standards:", err);
				setLaborStandards([]);
			});
		workOrderAttributesApi.getByCompany(companyId)
			.then((definitions) => {
				const active = definitions
					.filter((definition) => definition.isActive)
					.sort((a, b) => a.sortOrder - b.sortOrder);
				// The fields are filled from the campaign's product below.
				setAttributeDefinitions(active);
			})
			.catch((err) => {
				console.error("Failed to fetch work order attribute definitions:", err);
				setAttributeDefinitions([]);
			});
	}, [open, companyId, loadCampaigns]);

	const selectedCampaign = useMemo(
		() => campaigns.find((campaign) => campaign.id === selectedCampaignId) ?? null,
		[campaigns, selectedCampaignId]
	);

	// The campaign's product fills the order fields (batch from the sequence, product, strength…).
	const campaignProduct = useMemo(() => {
		const id = mode === "new" ? productId : selectedCampaign?.productId;
		return id === "" || id == null ? null : products.find((product) => product.id === id) ?? null;
	}, [mode, productId, selectedCampaign, products]);
	useEffect(() => {
		setAttributeValues((previous) => {
			const next = campaignAttributeDefaults(attributeDefinitions, campaignProduct);
			typedAttributes.forEach((key) => {
				next[key] = previous[key] ?? "";
			});
			return next;
		});
	}, [attributeDefinitions, campaignProduct, typedAttributes]);

	// The work center the campaign's standard runs on, when the plant has it and none is picked yet.
	const campaignStandardId = mode === "new" ? laborStandardId : selectedCampaign?.laborStandardId;
	useEffect(() => {
		if (workstation || campaignStandardId === "" || campaignStandardId == null) return;
		const standard = laborStandards.find((entry) => entry.id === campaignStandardId);
		const line = standard?.operations
			?.flatMap((operation) => [operation.workstation, ...(operation.workstationOptions ?? []).map((option) => option.workstation)])
			.find((name) => name && workcenters.includes(name));
		if (line) setWorkstation(line);
	}, [campaignStandardId, laborStandards, workcenters, workstation]);

	const productStandards = useMemo(
		() => (productId === ""
			? []
			: laborStandards.filter((standard) => standard.productId === productId)),
		[laborStandards, productId]
	);

	const handleSelectCampaign = (campaign: Campaign) => {
		setMode("existing");
		setSelectedCampaignId(campaign.id);
		if (!countTouched) {
			setCount(String(Math.max(1, campaign.targetCount - campaign.generatedCount)));
		}
	};

	const handleNewCampaign = () => {
		setMode("new");
		setSelectedCampaignId(null);
	};

	const handleTargetCountChange = (value: string) => {
		setTargetCount(value);
		// A brand-new campaign has nothing generated yet, so the default
		// generation count is the full target.
		if (!countTouched && Number(value) >= 1) {
			setCount(value);
		}
	};

	const resetState = useCallback(() => {
		setMode("existing");
		setSelectedCampaignId(null);
		setName("");
		setProductId("");
		setLaborStandardId("");
		setTargetCount("");
		setNotes("");
		setCount("1");
		setCountTouched(false);
		setStartDate(format(new Date(), "yyyy-MM-dd"));
		setWorkstation("");
		setSpacingDays("1");
		setQuantity("");
		setAttributeValues({});
		setTypedAttributes(new Set());
		setGenerating(false);
		setError(null);
		setResult(null);
	}, []);

	const handleClose = useCallback(() => {
		if (generating) return;
		resetState();
		onClose();
	}, [generating, resetState, onClose]);

	const countNum = Number(count);
	const spacingNum = parseFloat(spacingDays);
	const targetCountNum = Number(targetCount);
	const createValid = name.trim().length > 0 && productId !== "" && targetCountNum >= 1;
	const campaignReady = mode === "existing" ? selectedCampaign !== null : createValid;
	const generateValid =
		Number.isFinite(countNum) &&
		countNum >= 1 &&
		Boolean(startDate) &&
		Boolean(workstation) &&
		Number.isFinite(spacingNum) &&
		spacingNum > 0;

	const buildAttributeTemplate = (): string | undefined => {
		const template: Record<string, string> = {};
		attributeDefinitions.forEach((definition) => {
			const value = (attributeValues[definition.attrKey] || "").trim();
			if (value) template[definition.attrKey] = value;
		});
		return Object.keys(template).length > 0 ? JSON.stringify(template) : undefined;
	};

	const handleGenerate = async () => {
		if (!campaignReady || !generateValid) return;
		setGenerating(true);
		setError(null);
		try {
			let campaign = selectedCampaign;
			if (mode === "new") {
				campaign = await campaignsApi.create({
					companyId,
					name: name.trim(),
					productId: productId === "" ? undefined : productId,
					laborStandardId: laborStandardId === "" ? undefined : laborStandardId,
					targetCount: targetCountNum,
					notes: notes.trim() || undefined,
				});
			}
			if (!campaign) return;
			const previousCount = campaign.generatedCount ?? 0;
			const orders = await campaignsApi.generate(campaign.id, {
				count: countNum,
				startDate,
				workstation,
				spacingDays: spacingNum,
				quantity: quantity.trim() === "" ? undefined : Number(quantity),
				attributeValuesTemplate: buildAttributeTemplate(),
				locationId: locationId ?? undefined,
			});
			const sequences = Array.isArray(orders)
				? orders
					.map((order) => order.campaignSeq)
					.filter((seq) => typeof seq === "number" && Number.isFinite(seq))
				: [];
			const fromSeq = sequences.length > 0 ? Math.min(...sequences) : previousCount + 1;
			const toSeq = sequences.length > 0 ? Math.max(...sequences) : previousCount + countNum;
			// Refresh so generatedCount and the per-campaign progress are current
			let updated: Campaign = { ...campaign, generatedCount: previousCount + countNum };
			try {
				const refreshed = await campaignsApi.getByCompany(companyId);
				setCampaigns(refreshed);
				updated = refreshed.find((entry) => entry.id === campaign?.id) ?? updated;
			} catch (refreshError) {
				console.error("Failed to refresh campaigns after generating:", refreshError);
			}
			setResult({
				campaign: updated,
				createdCount: sequences.length > 0 ? sequences.length : countNum,
				fromSeq,
				toSeq,
			});
			setMode("existing");
			setSelectedCampaignId(campaign.id);
			setCountTouched(false);
			onGenerated();
		} catch (err: any) {
			console.error("Failed to generate campaign orders:", err);
			setError(
				err?.response?.data?.message ||
				err?.message ||
				"Failed to generate work orders. Please try again."
			);
		} finally {
			setGenerating(false);
		}
	};

	const handleGenerateMore = () => {
		setResult(null);
		setError(null);
		if (selectedCampaign) {
			setCount(String(Math.max(1, selectedCampaign.targetCount - selectedCampaign.generatedCount)));
		}
	};

	const renderCampaignRow = (campaign: Campaign) => {
		const isSelected = mode === "existing" && selectedCampaignId === campaign.id;
		return (
			<Box
				key={campaign.id}
				onClick={() => handleSelectCampaign(campaign)}
				sx={{
					border: `1px solid ${isSelected ? planner.accent : planner.hairline}`,
					borderRadius: `${planner.radiusSm}px`,
					bgcolor: isSelected ? planner.accentSoft : planner.surface,
					px: 1.5,
					py: 1,
					cursor: "pointer",
					transition: "border-color 120ms ease, background-color 120ms ease",
					"&:hover": { borderColor: isSelected ? planner.accent : planner.hairlineStrong },
				}}
			>
				<Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
					<Typography
						sx={{
							...planner.type.bodyStrong,
							color: planner.ink,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							minWidth: 0,
						}}
					>
						{campaign.name}
					</Typography>
					{campaign.productName ? (
						<MonoText sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
							{campaign.productName}
						</MonoText>
					) : null}
				</Box>
				<Box sx={{ mt: 0.75 }}>
					<ProgressMeter
						value={campaign.generatedCount}
						total={campaign.targetCount}
						state={campaignProgressState(campaign)}
					/>
				</Box>
			</Box>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={generating ? undefined : handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					minHeight: 480,
					borderRadius: `${planner.radius}px`,
					boxShadow: planner.shadowDialog,
				},
			}}
		>
			<DialogTitle>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<LayersIcon sx={{ color: planner.accent }} />
					<Box>
						<Typography sx={{ ...planner.type.title, color: planner.ink }}>
							Campaigns
						</Typography>
						<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
							Generate a sequenced series of identical work orders
						</Typography>
					</Box>
				</Box>
			</DialogTitle>

			<DialogContent sx={formControlsSx}>
				{error ? (
					<Alert
						severity="error"
						icon={false}
						sx={{
							...planner.type.body,
							mb: 2,
							borderRadius: `${planner.radiusSm}px`,
							border: `${planner.borderWidth}px solid ${planner.unstaffed}55`,
							bgcolor: planner.unstaffedSoft,
							color: planner.unstaffed,
						}}
						onClose={() => setError(null)}
					>
						{error}
					</Alert>
				) : null}

				{result ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							textAlign: "center",
							py: 5,
						}}
					>
						<CheckCircleIcon sx={{ fontSize: 48, color: planner.staffed, mb: 1.5 }} />
						<Typography sx={{ ...planner.type.title, color: planner.ink }}>
							Created {result.createdCount} order{result.createdCount !== 1 ? "s" : ""} (#{result.fromSeq}–#{result.toSeq})
						</Typography>
						<Typography sx={{ ...planner.type.body, color: planner.inkSecondary, mt: 0.5 }}>
							{result.campaign.name}
							{result.campaign.productName ? ` · ${result.campaign.productName}` : ""}
						</Typography>
						<Box sx={{ width: "100%", maxWidth: 340, mt: 3, textAlign: "left" }}>
							<SectionLabel sx={{ mb: 0.75 }}>Campaign progress</SectionLabel>
							<ProgressMeter
								value={result.campaign.generatedCount}
								total={result.campaign.targetCount}
								state={campaignProgressState(result.campaign)}
							/>
						</Box>
					</Box>
				) : (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
							gap: 3,
							mt: 0.5,
						}}
					>
						{/* Step 1 — pick or create the campaign */}
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
							<SectionLabel>1 · Campaign</SectionLabel>
							{loadingCampaigns ? (
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
									<CircularProgress size={16} sx={{ color: planner.accent }} />
									<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
										Loading campaigns…
									</Typography>
								</Box>
							) : campaigns.length > 0 ? (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 1,
										maxHeight: 240,
										overflowY: "auto",
										pr: 0.5,
									}}
								>
									{campaigns.map(renderCampaignRow)}
								</Box>
							) : (
								<Typography sx={{ ...planner.type.meta, color: planner.inkMuted }}>
									No campaigns yet — create the first one below.
								</Typography>
							)}

							{mode === "new" ? (
								<PlannerCard sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
									<SectionLabel>New campaign</SectionLabel>
									<TextField
										size="small"
										fullWidth
										label="Name"
										required
										value={name}
										onChange={(event) => setName(event.target.value)}
										placeholder="e.g. APAP 10mg batch series"
									/>
									<TextField
										select
										size="small"
										fullWidth
										label="Product"
										required
										value={productId}
										SelectProps={productSearch.selectProps}
										onChange={(event) => {
											const value = event.target.value === "" ? "" : Number(event.target.value);
											setProductId(value);
											setLaborStandardId("");
										}}
									>
										<ProductMenuSearch query={productSearch.query} onQueryChange={productSearch.setQuery} />
										{productSearch.visible.map((product) => (
											<MenuItem key={product.id} value={product.id}>
												{product.itemNumber ? `${product.itemNumber} · ${product.name}` : product.name}
											</MenuItem>
										))}
										{productSearch.isEmpty && <ProductMenuEmpty />}
									</TextField>
									<TextField
										select
										size="small"
										fullWidth
										label="Labor standard"
										value={laborStandardId}
										onChange={(event) =>
											setLaborStandardId(event.target.value === "" ? "" : Number(event.target.value))
										}
										disabled={productId === ""}
										helperText={
											productId === ""
												? "Pick a product first"
												: productStandards.length === 0
													? "No standards for this product"
													: undefined
										}
									>
										<MenuItem value="">No labor standard</MenuItem>
										{productStandards.map((standard) => (
											<MenuItem key={standard.id} value={standard.id}>
												{standard.description || standard.sku || `Standard ${standard.id}`} · {standard.workstation}
											</MenuItem>
										))}
									</TextField>
									<TextField
										size="small"
										fullWidth
										type="number"
										label="Target count"
										required
										value={targetCount}
										onChange={(event) => handleTargetCountChange(event.target.value)}
										inputProps={{ min: 1 }}
										helperText="Total orders this campaign should produce"
									/>
									<TextField
										size="small"
										fullWidth
										label="Notes"
										multiline
										minRows={2}
										value={notes}
										onChange={(event) => setNotes(event.target.value)}
									/>
								</PlannerCard>
							) : (
								<GhostButton
									size="small"
									startIcon={<AddIcon sx={{ fontSize: 16 }} />}
									onClick={handleNewCampaign}
									sx={{ alignSelf: "flex-start" }}
								>
									New campaign
								</GhostButton>
							)}
						</Box>

						{/* Step 2 — generate the next sequenced batch */}
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
							<SectionLabel>2 · Generate batches</SectionLabel>
							{!campaignReady ? (
								<Typography sx={{ ...planner.type.meta, color: planner.inkMuted }}>
									Pick a campaign — or fill in the new one — to set up generation.
								</Typography>
							) : null}
							<TextField
								size="small"
								fullWidth
								type="number"
								label="Count"
								required
								value={count}
								onChange={(event) => {
									setCount(event.target.value);
									setCountTouched(true);
								}}
								inputProps={{ min: 1 }}
								helperText={
									selectedCampaign
										? `${Math.max(0, selectedCampaign.targetCount - selectedCampaign.generatedCount)} of ${selectedCampaign.targetCount} remaining`
										: "How many orders to generate now"
								}
							/>
							<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
								<TextField
									size="small"
									fullWidth
									type="date"
									label="Start date"
									required
									value={startDate}
									onChange={(event) => setStartDate(event.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
								<TextField
									size="small"
									fullWidth
									type="number"
									label="Spacing (days)"
									value={spacingDays}
									onChange={(event) => setSpacingDays(event.target.value)}
									inputProps={{ min: 0.1, step: 0.5 }}
									helperText="0.5 = two per day"
								/>
							</Box>
							<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
								<TextField
									select
									size="small"
									fullWidth
									label="Work center"
									required
									value={workstation}
									onChange={(event) => setWorkstation(event.target.value)}
								>
									{workcenters.map((workcenter) => (
										<MenuItem key={workcenter} value={workcenter}>
											{workcenter}
										</MenuItem>
									))}
								</TextField>
								<TextField
									size="small"
									fullWidth
									type="number"
									label="Quantity per order"
									value={quantity}
									onChange={(event) => setQuantity(event.target.value)}
									inputProps={{ min: 1 }}
									helperText="Optional"
								/>
							</Box>
							{attributeDefinitions.length > 0 ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
									<Box>
										<SectionLabel>Attribute template</SectionLabel>
										<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 0.25 }}>
											Filled from the product; change any of them.{" "}
											<MonoText sx={{ color: planner.inkSecondary }}>{"{seq}"}</MonoText>
											{" "}is replaced with each order&apos;s batch number
										</Typography>
									</Box>
									{attributeDefinitions.map((definition) => (
										<TextField
											key={definition.attrKey}
											size="small"
											fullWidth
											label={definition.label}
											value={attributeValues[definition.attrKey] ?? ""}
											onChange={(event) => {
												const { value } = event.target;
												setTypedAttributes((prev) => new Set(prev).add(definition.attrKey));
												setAttributeValues((prev) => ({
													...prev,
													[definition.attrKey]: value,
												}));
											}}
											placeholder={definition.displaySlot === "headline" ? "{seq}" : undefined}
										/>
									))}
								</Box>
							) : null}
						</Box>
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				{result ? (
					<>
						<GhostButton size="small" onClick={handleGenerateMore}>
							Generate more
						</GhostButton>
						<PrimaryButton size="small" onClick={handleClose}>
							Done
						</PrimaryButton>
					</>
				) : (
					<>
						<GhostButton size="small" onClick={handleClose} disabled={generating}>
							Cancel
						</GhostButton>
						<PrimaryButton
							size="small"
							onClick={handleGenerate}
							disabled={!campaignReady || !generateValid || generating}
							startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <LayersIcon sx={{ fontSize: 16 }} />}
						>
							{generating
								? "Generating…"
								: Number.isFinite(countNum) && countNum >= 1
									? `Generate ${countNum} order${countNum === 1 ? "" : "s"}`
									: "Generate orders"}
						</PrimaryButton>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default CampaignDialog;
